import { useState, useEffect, useCallback, useRef } from 'react';
import { ICallerIdService } from '../interfaces/ICallerIdService';
import { ILineSelectionInfo } from '../types';
import { parsePhoneServicesFallback } from '../utils/phoneServicesFallback';
import { Logger } from '../../../packages/logger/src';
import { submitMetrics } from '../../../packages/widgets/src/services/WebexMetricsService';
import { setSelectedLineForCrossLaunch, getSelectedLineGuidForCrossLaunch, getSelectedLineIndexForCrossLaunch } from '../utils/selectedLineStore';

/**
 * Hook to manage line selection state.
 *
 * Depends on ICallerIdService (injected) — not on concrete service details.
 * This makes the hook testable: inject a MockCallerIdService in tests.
 *
 * Responsibilities:
 * - Fetch lines on mount (via service.getLines())
 * - Track selected line
 * - Handle line selection with optimistic update + rollback
 * - Fall back to Phone Services localStorage if fetch fails
 * - Detect external changes via visibility change, network reconnection,
 *   and Phone Services events (see design Section 16: External Change Propagation)
 */
export function useLineSelection(service: ICallerIdService) {
  const [lines, setLines] = useState<ILineSelectionInfo[]>([]);
  const [selectedLine, setSelectedLine] = useState<ILineSelectionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Shared fetch logic — used on mount and for external change re-fetches
  const refreshLines = useCallback(async (isInitialLoad = false) => {
    // Deduplicate concurrent refreshes (e.g. multiple visibility/online events)
    if (isRefreshingRef.current && !isInitialLoad) {
      Logger.debug('[LineSelection] Refresh already in progress — skipping');
      return;
    }
    isRefreshingRef.current = true;
    try {
      if (isInitialLoad) setIsLoading(true);

      // Wait for Webex SDK services to be fully ready
      // (isWebexInitalized flag is set before internal services finish registering,
      //  so we check for the actual _serviceUrls object)
      const isWebexReady = () => !!(window as any).webex?.internal?.services?._serviceUrls;
      if (!isWebexReady()) {
        Logger.debug('[LineSelection] Waiting for Webex SDK services (in MS Teams, this may take longer)...');
        await new Promise<void>((resolve, reject) => {
          const maxWaitMs = 30_000;
          const pollMs = 1000;
          let elapsed = 0;
          const timer = setInterval(() => {
            elapsed += pollMs;
            if (elapsed % 5000 === 0) {
              Logger.debug(`[LineSelection] Still waiting for Webex SDK (${elapsed}ms elapsed)`);
            }
            if (isWebexReady() || !mountedRef.current) {
              clearInterval(timer);
              resolve();
            } else if (elapsed >= maxWaitMs) {
              clearInterval(timer);
              reject(new Error('Webex SDK services not ready after 30s wait'));
            }
          }, pollMs);
        });
      }
      if (!mountedRef.current) return;
      Logger.info('[LineSelection] Webex SDK services ready — proceeding with fetch');

      const callerIdData = await service.getLines();
      if (!mountedRef.current) return;

      // Restore previous user selection from localStorage if available.
      // This persists the selection across page loads / tab switches.
      let activeLine: ILineSelectionInfo | null = null;
      const storedGuid = getSelectedLineGuidForCrossLaunch();
      const storedIndex = getSelectedLineIndexForCrossLaunch();
      if (storedGuid) {
        activeLine = callerIdData.lines.find(l => l.lineGuid === storedGuid) || null;
        if (activeLine) {
          Logger.info(`[LineSelection] Restored selection from stored GUID: ${activeLine.lineLabel} (${activeLine.title})`);
        }
      }
      if (!activeLine && storedIndex !== null) {
        // storedIndex is 1-based, lineIndex in data is 0-based
        activeLine = callerIdData.lines.find(l => l.lineIndex === storedIndex - 1) || null;
        if (activeLine) {
          Logger.info(`[LineSelection] Restored selection from stored lineIndex: ${activeLine.lineLabel} (${activeLine.title})`);
        }
      }
      if (!activeLine) {
        // No stored selection or stored line no longer exists — use service default
        const selected = callerIdData.lines.find(l => l.isSelected);
        activeLine = selected || callerIdData.lines[0] || null;
      }

      // Update isSelected flags on lines to match the active line,
      // and clear caller ID selections on non-selected lines
      const isActive = (l: ILineSelectionInfo) => activeLine ? l.lineIndex === activeLine.lineIndex : false;
      const updatedLines = callerIdData.lines.map(l => ({
        ...l,
        isSelected: isActive(l),
        callerIds: l.callerIds?.map(cid => ({
          ...cid,
          isSelected: isActive(l) ? cid.isSelected : false,
        })),
      }));
      setLines(updatedLines);
      setSelectedLine(activeLine ? { ...activeLine, isSelected: true } : null);
      setError(null);
      Logger.info(`[LineSelection] Fetched ${callerIdData.lines.length} lines, selected: ${activeLine?.lineLabel || 'none'} (${activeLine?.title || 'N/A'})`);
      if (callerIdData.lines.length <= 1) {
        Logger.info(`[LineSelection] Single line or no lines — LineSelector will be hidden`);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      Logger.error(`[LineSelection] Line fetch failed: ${err}`);
      setError('Failed to fetch lines');

      submitMetrics({ metricEvent: 'lineSelection.fetchFailed' });

      // Fallback: parse Phone Services data from localStorage
      if (isInitialLoad) {
        const fallbackLines = parsePhoneServicesFallback();
        if (fallbackLines.length > 0) {
          setLines(fallbackLines);
          setSelectedLine(fallbackLines[0]);
          // Same as above — don't store lineIndex on fallback load
          submitMetrics({ metricEvent: 'lineSelection.fallbackToPhoneServices' });
        }
      }
    } finally {
      isRefreshingRef.current = false;
      if (mountedRef.current && isInitialLoad) setIsLoading(false);
    }
  }, [service]);

  // Initial fetch on mount
  useEffect(() => {
    refreshLines(true);
  }, [refreshLines]);

  // ── External Change Detection ──────────────────────

  // Layer 1: Re-fetch when tab regains focus (covers desktop/mobile line changes)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        Logger.debug('[LineSelection] Tab focused — re-fetching CallerID');
        submitMetrics({ metricEvent: 'lineSelection.visibilityRefresh' });
        await refreshLines();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshLines]);

  // Layer 2: Re-fetch on network reconnection (covers sleep/wake, disconnect)
  useEffect(() => {
    const handleOnline = async () => {
      Logger.debug('[LineSelection] Network reconnected — re-fetching CallerID');
      submitMetrics({ metricEvent: 'lineSelection.networkReconnectRefresh' });
      await refreshLines();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [refreshLines]);

  // Layer 3: Re-fetch when Phone Services data changes (lines added/removed)
  useEffect(() => {
    const handlePhoneServicesChange = async () => {
      Logger.debug('[LineSelection] Phone Services changed — re-fetching CallerID');
      submitMetrics({ metricEvent: 'lineSelection.phoneServicesRefresh' });
      await refreshLines();
    };
    window.addEventListener('localstorage:phoneServices', handlePhoneServicesChange);
    return () => window.removeEventListener('localstorage:phoneServices', handlePhoneServicesChange);
  }, [refreshLines]);

  // ── End External Change Detection ────────────────────────────────

  // Select a line (and optionally a caller ID within it):
  // optimistic update → persist → rollback on failure
  // Returns true on success, false on failure or early return
  const selectLine = useCallback(async (lineIndex: number, callerIdIndex?: number): Promise<boolean> => {
    const line = lines.find(l => l.lineIndex === lineIndex);
    if (!line) {
      Logger.error(`[LineSelection] selectLine — lineIndex ${lineIndex} not found in ${lines.length} lines`);
      return false;
    }

    // Skip if the exact same selection (line + caller ID) is already active
    if (line.isSelected && callerIdIndex === undefined) {
      Logger.debug(`[LineSelection] selectLine — ${line.lineLabel} already selected, skipping`);
      return true; // Already selected is not a failure
    }
    if (line.isSelected && callerIdIndex !== undefined) {
      const cid = line.callerIds?.find(c => c.callerIdIndex === callerIdIndex);
      if (cid?.isSelected) {
        Logger.debug(`[LineSelection] selectLine — ${line.lineLabel} callerId ${callerIdIndex} already selected, skipping`);
        return true; // Already selected is not a failure
      }
    }

    const previousLines = lines;
    const previousSelection = selectedLine;

    // Optimistic update
    const updatedLine = { ...line, isSelected: true };
    setSelectedLine(updatedLine);
    setSelectedLineForCrossLaunch(updatedLine);
    setLines(prev => prev.map(l => ({
      ...l,
      isSelected: l.lineIndex === lineIndex,
      callerIds: l.callerIds?.map(cid => ({
        ...cid,
        // For the selected line: mark the chosen caller ID (or preserve if none specified)
        // For other lines: clear all caller ID selections
        isSelected: l.lineIndex === lineIndex
          ? (callerIdIndex !== undefined ? cid.callerIdIndex === callerIdIndex : cid.isSelected)
          : false,
      })),
    })));

    try {
      await service.setLine(line);
      Logger.info(`[LineSelection] Line set to ${line.lineLabel} (${line.lineType})${callerIdIndex !== undefined ? ` callerId=${callerIdIndex}` : ''}`);
      submitMetrics({ metricEvent: 'lineSelection.lineSelected' });
      return true;
    } catch (err) {
      Logger.error(`[LineSelection] Line selection failed: ${err}`);
      // Revert optimistic update
      setSelectedLine(previousSelection);
      setSelectedLineForCrossLaunch(previousSelection);
      setLines(previousLines);
      setError('Failed to set line');
      submitMetrics({ metricEvent: 'lineSelection.lineSelectionFailed' });
      return false;
    }
  }, [lines, selectedLine, service]);

  return {
    lines,
    selectedLine,
    selectLine,
    refreshLines,      // Exposed for manual refresh if needed
    isLoading,
    error,
    hasMultipleLines: lines.length > 1,
  };
}
